import { useGetList } from "ra-core";
import {
  BarChart3,
  DollarSign,
  Clock,
  CheckCircle2,
  FileText,
  Target,
  Users,
  TrendingUp,
} from "lucide-react";
import { PipelineCard } from "./PipelineCard";
import { ConversionFunnel } from "./ConversionFunnel";
import { RevenueBreakdown } from "./RevenueBreakdown";

interface Estimate {
  id: string;
  status: string;
  total: number;
}

interface Invoice {
  id: string;
  status: string;
  total: number;
  invoice_type: string;
}

interface Job {
  id: string;
  status: string;
  estimated_total: number;
  actual_cost: number;
}

interface Contact {
  id: string | number;
  contact_type: string;
  lifecycle_stage: string;
  lead_source: string;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Sales Pipeline Dashboard
 * Queries: estimates, invoices, jobs, contacts
 * Shows: confirmed income, possible income, weighted forecast,
 *        conversion funnel, revenue breakdown, marketing attribution
 */
export function PipelineDashboard() {
  const { data: estimates, isPending: estLoading } = useGetList<Estimate>("estimates", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: invoices, isPending: invLoading } = useGetList<Invoice>("invoices", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: jobs, isPending: jobsLoading } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: contacts, isPending: contactsLoading } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "id", order: "DESC" },
  });

  const loading = estLoading || invLoading || jobsLoading || contactsLoading;

  // === Income calculations ===
  const paidInvoices = invoices?.filter((i) => i.status === "paid") || [];
  const confirmedIncome = paidInvoices.reduce((sum, i) => sum + Number(i.total || 0), 0);

  const sentInvoices = invoices?.filter((i) => i.status === "sent") || [];
  const outstandingIncome = sentInvoices.reduce((sum, i) => sum + Number(i.total || 0), 0);

  const acceptedEstimates = estimates?.filter((e) => e.status === "accepted") || [];
  const acceptedTotal = acceptedEstimates.reduce((sum, e) => sum + Number(e.total || 0), 0);

  const pendingEstimates = estimates?.filter((e) => e.status === "sent" || e.status === "draft") || [];
  const possibleIncome = pendingEstimates.reduce((sum, e) => sum + Number(e.total || 0), 0);

  // Weighted: accepted = 90%, sent = 40%, draft = 10%
  const weightedForecast =
    acceptedTotal * 0.9 +
    (estimates?.filter((e) => e.status === "sent").reduce((s, e) => s + Number(e.total || 0), 0) || 0) * 0.4 +
    (estimates?.filter((e) => e.status === "draft").reduce((s, e) => s + Number(e.total || 0), 0) || 0) * 0.1;

  const totalPipeline = confirmedIncome + outstandingIncome + possibleIncome;

  // === Job metrics ===
  const activeJobs = jobs?.filter((j) => j.status === "in_progress" || j.status === "scheduled") || [];
  const completedJobs = jobs?.filter((j) => j.status === "completed" || j.status === "closed") || [];
  const totalMargin = completedJobs.reduce((sum, j) => {
    const est = Number(j.estimated_total || 0);
    const cost = Number(j.actual_cost || 0);
    return sum + (est - cost);
  }, 0);
  const avgMarginPct =
    completedJobs.length > 0
      ? (totalMargin /
          completedJobs.reduce((s, j) => s + Number(j.estimated_total || 0), 0)) *
        100
      : 0;

  // === Funnel ===
  const totalLeads = contacts?.filter((c) => c.contact_type === "lead").length || 0;
  const totalCustomers = contacts?.filter((c) => c.contact_type === "customer").length || 0;
  const totalEstimates = estimates?.length || 0;
  const totalAccepted = acceptedEstimates.length;
  const totalJobsCount = jobs?.length || 0;

  const funnelStages = [
    { label: "Leads", count: totalLeads, color: "#6366f1" },
    { label: "Estimates Sent", count: totalEstimates, color: "#3b82f6" },
    { label: "Estimates Accepted", count: totalAccepted, color: "#06b6d4" },
    { label: "Jobs Created", count: totalJobsCount, color: "#10b981" },
    { label: "Jobs Completed", count: completedJobs.length, color: "#16a34a" },
  ];

  // === Revenue breakdown ===
  const revenueRows = [
    {
      label: "Confirmed (Paid)",
      amount: confirmedIncome,
      color: "#16a34a",
      count: paidInvoices.length,
    },
    {
      label: "Outstanding (Sent)",
      amount: outstandingIncome,
      color: "#d97706",
      count: sentInvoices.length,
    },
    {
      label: "Possible (Pending Estimates)",
      amount: possibleIncome,
      color: "#6366f1",
      count: pendingEstimates.length,
    },
  ];

  // === Lead source attribution ===
  const sourceMap = new Map<string, { leads: number; customers: number }>();
  (contacts || []).forEach((c) => {
    const src = c.lead_source || "unknown";
    const entry = sourceMap.get(src) || { leads: 0, customers: 0 };
    if (c.contact_type === "lead") entry.leads++;
    if (c.contact_type === "customer") entry.customers++;
    sourceMap.set(src, entry);
  });
  const sources = Array.from(sourceMap.entries())
    .map(([src, data]) => ({ source: src, ...data, total: data.leads + data.customers }))
    .sort((a, b) => b.total - a.total);

  return (
    <div style={{ padding: 24 }}>
      <div className="flex items-center gap-2 mb-6">
        <BarChart3 className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-bold">Sales Pipeline</h1>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground mb-4">Loading pipeline data…</p>
      )}

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <PipelineCard
          label="Confirmed Income"
          value={fmt(confirmedIncome)}
          sublabel={`${paidInvoices.length} paid invoices`}
          color="#16a34a"
          icon={CheckCircle2}
        />
        <PipelineCard
          label="Outstanding"
          value={fmt(outstandingIncome)}
          sublabel={`${sentInvoices.length} awaiting payment`}
          color="#d97706"
          icon={Clock}
        />
        <PipelineCard
          label="Possible Income"
          value={fmt(possibleIncome)}
          sublabel={`${pendingEstimates.length} pending estimates`}
          color="#6366f1"
          icon={FileText}
        />
        <PipelineCard
          label="Weighted Forecast"
          value={fmt(weightedForecast)}
          sublabel="90% accepted · 40% sent · 10% draft"
          color="#2563eb"
          icon={Target}
        />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <PipelineCard
          label="Active Jobs"
          value={activeJobs.length.toString()}
          sublabel={`${completedJobs.length} completed`}
          color="#06b6d4"
          icon={TrendingUp}
        />
        <PipelineCard
          label="Avg Margin"
          value={avgMarginPct > 0 ? `${avgMarginPct.toFixed(1)}%` : "—"}
          sublabel={`${fmt(totalMargin)} total margin`}
          color="#16a34a"
          icon={DollarSign}
          barPct={avgMarginPct}
        />
        <PipelineCard
          label="Close Rate"
          value={
            totalEstimates > 0
              ? `${((totalAccepted / totalEstimates) * 100).toFixed(1)}%`
              : "—"
          }
          sublabel={`${totalAccepted} of ${totalEstimates} estimates`}
          color="#7c3aed"
          icon={Target}
          barPct={totalEstimates > 0 ? (totalAccepted / totalEstimates) * 100 : 0}
        />
        <PipelineCard
          label="Total Pipeline"
          value={fmt(totalPipeline)}
          sublabel="All stages combined"
          color="#0f172a"
          icon={BarChart3}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ConversionFunnel stages={funnelStages} />
        <RevenueBreakdown rows={revenueRows} total={totalPipeline} />
      </div>

      {/* Marketing Attribution */}
      {sources.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Lead Source Attribution
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left px-4 py-2 font-medium">Source</th>
                  <th className="text-right px-3 py-2 font-medium">Leads</th>
                  <th className="text-right px-3 py-2 font-medium">Customers</th>
                  <th className="text-right px-3 py-2 font-medium">Conversion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sources.map((s) => (
                  <tr key={s.source} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium capitalize">
                      {s.source.replace(/_/g, " ")}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.leads}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.customers}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {s.total > 0
                        ? `${((s.customers / s.total) * 100).toFixed(0)}%`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

PipelineDashboard.path = "/pipeline";
